import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingCart, AlertCircle, CheckCircle, Search, ArrowLeft, Loader2 } from 'lucide-react';
import mesaOrderAPI, {
  Product,
  Menu,
  Store,
  Mesa,
  MesaOrderItem,
} from '../services/mesaOrderAPI';
import ProductGrid from './ProductGrid';
import { orderMenu } from '../utils/menuOrder';
import { applyMenuColors } from '../utils/menuColors';
import CartDrawer from './CartDrawer';
import { mergeFractionalPizzas, CartItem } from '../utils/pizzaMergeUtils';
import { getDirectImageUrl } from '../utils/imageUrl';
import SearchModal from './SearchModal';

type PageState = 'loading' | 'menu' | 'confirm' | 'success' | 'error';
type TabType = 'home' | 'cart';

const MesaOrderPage: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [store, setStore] = useState<Store | null>(null);
  const [mesa, setMesa] = useState<Mesa | null>(null);
  const [menu, setMenu] = useState<Menu>({});
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [highlightedProductId, setHighlightedProductId] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<TabType>('home');
  const [observacoes, setObservacoes] = useState('');

  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const productRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const scrollTabIntoView = useCallback((category: string) => {
    const tabElement = tabRefs.current[category];
    const containerElement = tabsContainerRef.current;

    if (tabElement && containerElement) {
      const tabLeft = tabElement.offsetLeft;
      const tabWidth = tabElement.offsetWidth;
      const containerWidth = containerElement.offsetWidth;
      const targetScrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2);

      containerElement.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth'
      });
    }
  }, []);

  useEffect(() => {
    if (linkId) {
      loadMesaMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkId]);

  useEffect(() => {
    if (pageState !== 'menu') return;

    const handleScroll = () => {
      const categories = Object.keys(menu);
      const scrollPosition = window.scrollY + 100;

      for (let i = categories.length - 1; i >= 0; i--) {
        const category = categories[i];
        const element = categoryRefs.current[category];

        if (element && element.offsetTop <= scrollPosition) {
          setSelectedCategory(category);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [menu, pageState]);

  useEffect(() => {
    if (selectedCategory) {
      scrollTabIntoView(selectedCategory);
    }
  }, [selectedCategory, scrollTabIntoView]);

  const loadMesaMenu = async () => {
    try {
      setPageState('loading');

      const validation = await mesaOrderAPI.validateMesaLink(linkId!);

      if (!validation.valid) {
        setErrorMessage(validation.error || 'Link inválido');
        setPageState('error');
        return;
      }

      setStore(validation.store!);
      applyMenuColors(validation.store!.cores_cardapio);
      setMesa(validation.mesa!);

      const menuData = await mesaOrderAPI.getMesaMenu(linkId!);

      const processedMenu: Menu = orderMenu(menuData.menu, menuData.category_order);

      setMenu(processedMenu);

      const categories = Object.keys(processedMenu);
      if (categories.length > 0) {
        setSelectedCategory(categories[0]);
      }

      setPageState('menu');
    } catch (error: any) {
      console.error('Error loading mesa menu:', error);
      setErrorMessage(error.response?.data?.error || 'Erro ao carregar cardápio');
      setPageState('error');
    }
  };

  const handleAddToCart = (
    product: Product,
    quantity: number,
    attributes?: Record<string, any>,
    finalPrice?: number
  ) => {
    setCartItems((prev) => {
      const newItemSignature = JSON.stringify({
        productId: product.id,
        attributes: attributes || {}
      });

      const existingIndex = prev.findIndex((item: any) => {
        const itemSignature = JSON.stringify({
          productId: item.product.id,
          attributes: item.product_variables || {}
        });
        return itemSignature === newItemSignature;
      });

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity += quantity;

        if (updated[existingIndex].quantity <= 0) {
          return updated.filter((_, index) => index !== existingIndex);
        }

        return updated;
      } else {
        if (quantity > 0) {
          return [
            ...prev,
            {
              product,
              quantity,
              product_variables: attributes || {},
              finalPrice: finalPrice
            }
          ];
        }
        return prev;
      }
    });
  };

  const getItemSignature = (productId: string, attributes?: Record<string, any>): string => {
    return JSON.stringify({
      productId: productId,
      attributes: attributes || {}
    });
  };

  const handleUpdateQuantity = (productId: string, attributes: Record<string, any> | undefined, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(productId, attributes);
      return;
    }

    const targetSignature = getItemSignature(productId, attributes);
    setCartItems((prev) =>
      prev.map((item: any) => {
        const itemSignature = getItemSignature(item.product.id, item.product_variables);
        return itemSignature === targetSignature ? { ...item, quantity } : item;
      })
    );
  };

  const handleRemoveItem = (productId: string, attributes?: Record<string, any>) => {
    const targetSignature = getItemSignature(productId, attributes);
    setCartItems((prev) => prev.filter((item: any) => {
      const itemSignature = getItemSignature(item.product.id, item.product_variables);
      return itemSignature !== targetSignature;
    }));
  };

  const handleRemoveMergedItem = (mergedItem: CartItem) => {
    const originalItems = (mergedItem as any).items || [];
    if (originalItems.length === 0) return;

    setCartItems((prev) => {
      return prev.filter((item: any) => {
        const itemSignature = getItemSignature(item.product.id, item.product_variables);

        return !originalItems.some((originalItem: CartItem) => {
          const originalSignature = getItemSignature(originalItem.product.id, originalItem.product_variables);
          return itemSignature === originalSignature;
        });
      });
    });
  };

  const handleGoToConfirm = () => {
    if (cartItems.length === 0) return;
    setPageState('confirm');
    setIsCartOpen(false);
  };

  const handleSubmitOrder = async () => {
    try {
      setIsSubmitting(true);

      const items: MesaOrderItem[] = cartItems.map((item: any) => ({
        product_id: item.product.id,
        quantity: item.quantity,
        product_variables: item.product_variables || {}
      }));

      const response = await mesaOrderAPI.createMesaOrder(linkId!, {
        items,
        observacoes: observacoes || undefined,
      });

      if (response.success) {
        setOrderNumber(response.daily_order_id);
        setPageState('success');
      }
    } catch (error: any) {
      console.error('Error creating mesa order:', error);
      setErrorMessage(error.response?.data?.error || 'Erro ao criar pedido');
      setPageState('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const mergedCartItems = useMemo(() => {
    return mergeFractionalPizzas(cartItems, store?.cobrar_preco_maior);
  }, [cartItems, store?.cobrar_preco_maior]);

  const getTotalItems = (): number => {
    return mergedCartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const calculateTotal = (): number => {
    return mergedCartItems.reduce((total, item) => {
      const price = item.finalPrice || item.product.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const scrollToCategory = (category: string) => {
    const element = categoryRefs.current[category];
    if (element) {
      const headerOffset = 70;
      const elementPosition = element.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setSelectedCategory(category);
    }
  };

  const handleProductSelect = (productId: string, category: string) => {
    const productElement = productRefs.current[productId];

    if (productElement) {
      const headerOffset = 70;
      const elementPosition = productElement.offsetTop;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      setHighlightedProductId(productId);
      setTimeout(() => setHighlightedProductId(null), 2000);
    }

    setIsSearchOpen(false);
    setSelectedCategory(category);
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando cardápio...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Ops!</h1>
          <p className="text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Pedido Enviado!</h1>
          <p className="text-gray-600 mb-4">
            Seu pedido #{orderNumber} foi recebido com sucesso.
          </p>
          <div className="bg-red-50 p-4 rounded-lg mb-6">
            <p className="text-red-800 font-semibold">{mesa?.name}</p>
          </div>
          <p className="text-gray-500 text-sm">
            Em breve seu pedido estará pronto!
          </p>
        </div>
      </div>
    );
  }

  // Confirm state (simplified checkout)
  if (pageState === 'confirm') {
    return (
      <div className="min-h-screen bg-gray-50 pb-20">
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setPageState('menu')}
                disabled={isSubmitting}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">Confirmar Pedido</h1>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6">
          {/* Mesa indicator */}
          <div className="bg-red-50 p-4 rounded-lg mb-6 text-center">
            <span className="font-bold text-red-800 text-lg">{mesa?.name}</span>
          </div>

          {/* Order summary */}
          <div className="bg-white p-4 rounded-lg border mb-6">
            <h2 className="font-bold mb-4 text-gray-900">Resumo do Pedido</h2>
            <div className="space-y-3">
              {mergedCartItems.map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b last:border-b-0">
                  <div className="flex-1">
                    <span className="text-gray-800">{item.quantity}x {item.product.name}</span>
                    {item.product_variables && Object.keys(item.product_variables).length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        {Object.entries(item.product_variables).map(([key, value]) => (
                          <span key={key}>
                            {key}: {Array.isArray(value) ? value.join(', ') : value}
                          </span>
                        )).reduce((prev, curr, i) => i === 0 ? [curr] : [...prev, ' | ', curr], [] as React.ReactNode[])}
                      </p>
                    )}
                  </div>
                  <span className="font-semibold text-gray-900">
                    R$ {((item.finalPrice || item.product.price) * item.quantity).toFixed(2).replace('.', ',')}
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t mt-4 pt-4 flex justify-between font-bold text-lg">
              <span>Total</span>
              <span className="text-red-600">R$ {calculateTotal().toFixed(2).replace('.', ',')}</span>
            </div>
          </div>

          {/* Observacoes */}
          <div className="bg-white p-4 rounded-lg border mb-6">
            <label className="block font-medium mb-2 text-gray-900">Observacoes (opcional)</label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              className="w-full border rounded-lg p-3 text-gray-800 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Alguma observacao sobre seu pedido?"
              disabled={isSubmitting}
            />
          </div>

          {/* Confirm button */}
          <button
            onClick={handleSubmitOrder}
            disabled={isSubmitting}
            className="w-full bg-red-600 text-white py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Enviando pedido...
              </>
            ) : (
              'Confirmar Pedido'
            )}
          </button>
        </div>
      </div>
    );
  }

  // Menu state
  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        {/* Banner */}
        {store?.banner_image_path && (
          <div className="w-full h-32 sm:h-40 overflow-hidden">
            <img
              src={getDirectImageUrl(store.banner_image_path)}
              alt="Banner"
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Mesa Badge */}
        <div className="bg-red-600 text-white text-center py-2">
          <span className="font-bold">{mesa?.name}</span>
        </div>

        {/* Store info */}
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            {store?.image_path && (
              <img
                src={getDirectImageUrl(store.image_path)}
                alt={store.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-white shadow"
              />
            )}
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{store?.name}</h1>
              {store?.endereco && (
                <p className="text-sm text-gray-500">{store.endereco}</p>
              )}
            </div>
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <Search className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Category tabs */}
        <div
          ref={tabsContainerRef}
          className="flex gap-2 px-4 pb-3 overflow-x-auto scrollbar-hide"
        >
          {Object.keys(menu).map((category) => (
            <button
              key={category}
              ref={(el) => (tabRefs.current[category] = el)}
              onClick={() => scrollToCategory(category)}
              className={`min-h-[42px] px-4 py-2 rounded-md whitespace-nowrap text-[15px] font-bold border-[1.5px] transition-colors ${
                selectedCategory === category
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-transparent text-gray-900 border-gray-900 hover:bg-gray-100'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Menu content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {Object.entries(menu).map(([category, products]) => (
          <div
            key={category}
            ref={(el) => (categoryRefs.current[category] = el)}
            className="mb-8"
          >
            <h2 className="text-[28px] leading-none font-extrabold tracking-tight text-gray-900 mb-4">{category}</h2>
            <ProductGrid
              products={products}
              onAddToCart={handleAddToCart}
              cartItems={new Map(cartItems.map(item => [item.product.id, item.quantity]))}
              store={store || undefined}
              menu={menu}
              productRefs={productRefs}
              highlightedProductId={highlightedProductId}
            />
          </div>
        ))}
      </div>

      {/* Bottom Navigation - Only Home and Cart */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-50">
        <div className="max-w-4xl mx-auto flex justify-around">
          <button
            onClick={() => {
              setCurrentTab('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`flex flex-col items-center py-2 px-4 ${
              currentTab === 'home' ? 'text-red-600' : 'text-gray-500'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-xs mt-1">Cardápio</span>
          </button>

          <button
            onClick={() => setIsCartOpen(true)}
            className="flex flex-col items-center py-2 px-4 text-gray-500 relative"
          >
            <ShoppingCart className="w-6 h-6" />
            {getTotalItems() > 0 && (
              <span className="absolute -top-1 right-2 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {getTotalItems()}
              </span>
            )}
            <span className="text-xs mt-1">Carrinho</span>
          </button>
        </div>
      </div>

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={mergedCartItems}
        onUpdateQuantity={handleUpdateQuantity}
        onRemoveItem={handleRemoveItem}
        onRemoveMergedItem={handleRemoveMergedItem}
        onCheckout={handleGoToConfirm}
        store={store || undefined}
      />

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        menu={menu}
        onProductSelect={handleProductSelect}
      />
    </div>
  );
};

export default MesaOrderPage;
